import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  MapPin, Sparkles, MessageCircle, BadgeCheck, Sofa, BedDouble, Car,
  Droplets, Wind, Timer, Star, Camera, CalendarCheck, Search,
  CheckCircle2, AlertCircle, ArrowRight,
} from "lucide-react";
import { SEO } from "@/lib/seo";
import { waLink, DEFAULT_WA_MESSAGE } from "@/lib/whatsapp";
import { SERVICES, getService } from "@/data/services";
import { Reveal } from "@/components/Reveal";
import BeforeAfter from "@/components/BeforeAfter";

const LANDING_SERVICES = [
  {
    slug: "limpieza-muebles",
    icon: Sofa,
    title: "Lavado de muebles",
    text: "Eliminamos suciedad, manchas, malos olores y acumulación de polvo mediante un proceso profesional de limpieza.",
    extra: "Ideal para: sofás, salas, poltronas, sillas y otros muebles tapizados.",
    cta: "Cotizar lavado de muebles",
  },
  {
    slug: "limpieza-colchones",
    icon: BedDouble,
    title: "Lavado de colchones",
    text: "Realizamos una limpieza profunda para retirar suciedad, manchas y malos olores acumulados en el colchón.",
    extra: null,
    cta: "Cotizar lavado de colchón",
  },
  {
    slug: "limpieza-interior-vehiculos",
    icon: Car,
    title: "Lavado de interior de vehículos",
    text: "Limpiamos profundamente el interior de tu vehículo para recuperar su apariencia y frescura.",
    extra: "Incluye limpieza de: tapicería, sillas, alfombras y diferentes superficies interiores.",
    cta: "Cotizar limpieza de vehículo",
  },
];

const WHY_US = [
  { icon: Sparkles, text: "Servicio profesional directamente en tu domicilio" },
  { icon: MapPin, text: "Atención a domicilio en Medellín" },
  { icon: Droplets, text: "Procesos de limpieza profesional" },
  { icon: Wind, text: "Limpieza profunda" },
  { icon: Timer, text: "Servicio práctico y conveniente" },
  { icon: MessageCircle, text: "Atención rápida por WhatsApp" },
  { icon: Star, text: "Enfoque en resultados y satisfacción del cliente" },
];

const STEPS = [
  { icon: MessageCircle, num: "1", title: "Escríbenos por WhatsApp", text: "Cuéntanos qué necesitas limpiar." },
  { icon: Camera, num: "2", title: "Envíanos fotos", text: "Evaluamos el servicio y te damos información sobre el precio." },
  { icon: CalendarCheck, num: "3", title: "Agendamos tu visita", text: "Coordinamos día y hora." },
  { icon: Sparkles, num: "4", title: "Limpiamos en tu domicilio", text: "Nuestro equipo realiza el servicio profesionalmente." },
];

const AREAS = [
  "El Poblado", "Laureles", "Estadio", "Belén", "La Candelaria", "Robledo",
  "Castilla", "Buenos Aires", "San Javier", "La América", "Aranjuez", "Manrique",
  "Popular", "Santa Cruz", "Doce de Octubre", "Envigado", "Sabaneta", "Itagüí",
  "La Estrella", "Caldas", "Bello", "Copacabana", "Girardota", "Barbosa", "Rionegro",
];

const norm = (s) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

const Home = () => {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, [location]);

  const checkArea = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    const q = norm(query);
    const found = AREAS.find((a) => norm(a).includes(q) || q.includes(norm(a)));
    setResult(found ? { ok: true, area: found } : { ok: false });
  };

  return (
    <div data-testid="home-page">
      <SEO
        title="Lavado de muebles y colchones a domicilio en Medellín | Colors Limpieza"
        description="Lavado de muebles, colchones e interiores de vehículos a domicilio en Medellín. Servicio profesional de limpieza. Solicita tu cotización por WhatsApp."
      />

      {/* 1. HERO */}
      <section id="inicio" className="relative overflow-hidden bg-white" data-testid="hero-section">
        <div className="pointer-events-none absolute right-0 top-0 h-[36rem] w-[36rem] translate-x-1/3 -translate-y-1/4 rounded-full bg-brand-light" />
        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-14 sm:px-6 md:pt-24 lg:px-12">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-light px-4 py-1.5 text-xs font-semibold text-brand-dark">
                <MapPin size={14} /> Medellín y Valle de Aburrá
              </div>
              <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
                Limpieza profesional a domicilio en <span className="text-brand">Medellín</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-slate-600">
                Dejamos tus muebles, colchones y vehículo como nuevos, sin que tengas que salir de casa.
              </p>
              <div className="mt-7 flex flex-wrap gap-2.5">
                {LANDING_SERVICES.map((s) => (
                  <span key={s.slug} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700" data-testid={`hero-chip-${s.slug}`}>
                    <s.icon size={14} className="text-brand" /> {s.title}
                  </span>
                ))}
              </div>
              <div className="mt-9">
                <a
                  href={waLink(DEFAULT_WA_MESSAGE)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-9 py-4 text-sm font-bold text-white shadow-[0_8px_25px_-5px_rgba(37,211,102,0.4)] transition-all hover:-translate-y-0.5 hover:bg-[#1EBE5A]"
                  data-testid="hero-whatsapp-button"
                >
                  <MessageCircle size={18} /> Solicitar servicio por WhatsApp
                </a>
                <p className="mt-3 text-xs text-slate-400">Respuesta rápida en horario de atención · Sin compromiso</p>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="relative mx-auto max-w-sm">
                <div className="absolute inset-x-8 top-10 bottom-0 rounded-t-full bg-brand/15" />
                <img
                  src="https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Sala impecable después de una limpieza profesional de muebles"
                  className="relative aspect-[3/4] w-full rounded-t-full rounded-b-3xl object-cover shadow-2xl"
                  data-testid="hero-image"
                />
                <div className="absolute -left-6 top-16 -rotate-6 rounded-2xl bg-sunset px-4 py-2.5 font-display text-sm font-bold text-white shadow-lg sm:-left-12">
                  ¡Sin salir de casa!
                </div>
                <div className="absolute -right-4 bottom-24 rotate-3 rounded-2xl bg-white px-4 py-3 shadow-xl sm:-right-10">
                  <p className="flex items-center gap-1.5 font-display text-sm font-bold text-slate-900">
                    <BadgeCheck size={16} className="text-brand" /> Servicio a domicilio
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">En todo Medellín</p>
                </div>
                <div className="absolute -bottom-8 left-1/2 flex h-24 w-24 -translate-x-1/2 animate-float-slow items-center justify-center rounded-full border-4 border-white bg-brand-light shadow-xl">
                  <img src="/logo.png" alt="Logo Colors Limpieza" className="h-20 w-20 rounded-full object-cover" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 2. SERVICIOS */}
      <section id="servicios" className="bg-[#F8FAFC] py-16 md:py-24" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <div className="text-center">
              <p className="text-xs font-semibold uppercase tracking-widest text-brand">Nuestros servicios</p>
              <h2 className="mx-auto mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                ¿Qué necesitas limpiar hoy?
              </h2>
            </div>
          </Reveal>
          <div className="mt-16 space-y-16 md:space-y-20">
            {LANDING_SERVICES.map((ls, i) => {
              const service = getService(ls.slug);
              return (
                <Reveal key={ls.slug} delay={0.05}>
                  <div className={`grid items-center gap-8 md:grid-cols-2 md:gap-14 ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`} data-testid={`service-card-${ls.slug}`}>
                    <Link to={`/servicios/${ls.slug}`} className="group relative block">
                      <div className={`absolute inset-0 translate-x-3 translate-y-3 rounded-[2.5rem] ${i % 2 === 1 ? "bg-sunset-light" : "bg-brand-light"}`} />
                      <img
                        src={service.image}
                        alt={ls.title}
                        className="relative aspect-[4/3] w-full rounded-[2.5rem] object-cover shadow-lg transition-transform duration-500 group-hover:-translate-y-1"
                        loading="lazy"
                      />
                      <span className="absolute -top-4 left-6 flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg">
                        <ls.icon size={20} />
                      </span>
                    </Link>
                    <div>
                      <h3 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{ls.title}</h3>
                      <p className="mt-3 max-w-md text-base leading-relaxed text-slate-600">{ls.text}</p>
                      {ls.extra && (
                        <p className="mt-3 max-w-md rounded-2xl bg-brand-light px-4 py-3 text-sm font-medium text-brand-dark">{ls.extra}</p>
                      )}
                      <div className="mt-6 flex flex-wrap gap-3">
                        <a
                          href={waLink(service.waMessage)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-[#1EBE5A]"
                          data-testid={`service-whatsapp-${ls.slug}`}
                        >
                          <MessageCircle size={16} /> {ls.cta}
                        </a>
                        <Link
                          to={`/servicios/${ls.slug}`}
                          className="inline-flex items-center gap-2 rounded-full border-2 border-slate-200 px-6 py-3 text-sm font-bold text-slate-700 transition-colors hover:border-brand hover:text-brand-dark"
                          data-testid={`service-detail-${ls.slug}`}
                        >
                          Ver detalle <ArrowRight size={15} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. POR QUÉ ELEGIRNOS */}
      <section id="por-que" className="bg-slate-900 py-16 md:py-24" data-testid="benefits-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              ¿Por qué elegirnos?
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_US.map((item, i) => (
              <Reveal key={item.text} delay={i * 0.06} className={i === WHY_US.length - 1 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <div
                  className="flex h-full items-center gap-4 rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur transition-all duration-300 hover:border-brand/40 hover:bg-white/10"
                  data-testid={`benefit-card-${i}`}
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand/20">
                    <item.icon size={20} className="text-brand" />
                  </div>
                  <p className="text-sm font-medium leading-relaxed text-slate-200">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4. ANTES Y DESPUÉS */}
      <section id="antes-despues" className="bg-white py-16 md:py-24" data-testid="before-after-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-widest text-brand">Antes y después</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Mira la diferencia
            </h2>
            <p className="mt-3 max-w-xl text-base text-slate-600">
              Desliza el comparador y descubre cómo queda un mueble, colchón o vehículo después de una limpieza profunda profesional.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            <Reveal>
              <BeforeAfter image={getService("limpieza-muebles").image} title="Muebles" subtitle="Sofás, salas y tapizados" />
            </Reveal>
            <Reveal delay={0.1}>
              <BeforeAfter image={getService("limpieza-colchones").image} title="Colchones" subtitle="Desinfección y frescura" />
            </Reveal>
            <Reveal delay={0.2}>
              <BeforeAfter image={getService("limpieza-interior-vehiculos").galleryImage} title="Vehículos" subtitle="Tapicería e interiores" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* 5. CÓMO FUNCIONA */}
      <section id="como-funciona" className="bg-[#F8FAFC] py-16 md:py-24" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-widest text-brand">Cómo funciona</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Solicitar tu servicio es muy fácil
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1}>
                <div className="relative h-full rounded-3xl bg-white p-6 shadow-[0_10px_30px_-10px_rgba(0,183,205,0.12)] md:p-7" data-testid={`process-step-${s.num}`}>
                  <span className="absolute right-6 top-5 font-display text-5xl font-extrabold text-slate-100">{s.num}</span>
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sunset-light">
                    <s.icon size={22} className="text-sunset-dark" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-10 text-center">
              <a
                href={waLink(DEFAULT_WA_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-bold text-white shadow-[0_8px_25px_-5px_rgba(37,211,102,0.4)] transition-all hover:-translate-y-0.5 hover:bg-[#1EBE5A]"
                data-testid="process-whatsapp-button"
              >
                <MessageCircle size={18} /> Empezar con el paso 1
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. ZONA DE ATENCIÓN */}
      <section id="cobertura" className="bg-white py-16 md:py-24" data-testid="coverage-teaser-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem]">
              <img
                src="https://images.unsplash.com/photo-1709152213008-71edae302aad?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Panorámica de Medellín, zona de atención de Colors Limpieza"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                data-testid="coverage-teaser-image"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/30" />
              <div className="relative grid gap-10 p-8 md:p-14 lg:grid-cols-2 lg:p-16">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur">
                    <MapPin size={14} /> Zona de atención
                  </div>
                  <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                    Servicio de limpieza a domicilio en Medellín
                  </h2>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-300">
                    Atendemos diferentes sectores de Medellín y zonas cercanas, sujeto a cobertura y disponibilidad.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2.5">
                    {AREAS.slice(0, 10).map((a) => (
                      <span key={a} className="rounded-full bg-white/15 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur" data-testid={`area-badge-${a}`}>
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="rounded-3xl bg-white/95 p-6 backdrop-blur md:p-8">
                  <h3 className="font-display text-lg font-bold text-slate-900">¿Llegamos a tu sector?</h3>
                  <p className="mt-1.5 text-sm text-slate-600">Escribe tu barrio o municipio y verifícalo al instante.</p>
                  <form onSubmit={checkArea} className="mt-5 flex gap-2">
                    <input
                      type="text"
                      placeholder="Ej: Envigado, Laureles, Sabaneta..."
                      className="w-full rounded-full border border-slate-200 bg-white px-5 py-3.5 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-brand focus:ring-2 focus:ring-brand/20"
                      value={query}
                      onChange={(e) => { setQuery(e.target.value); setResult(null); }}
                      data-testid="coverage-checker-input"
                    />
                    <button
                      type="submit"
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand text-white transition-colors hover:bg-brand-dark"
                      aria-label="Verificar zona"
                      data-testid="coverage-checker-button"
                    >
                      <Search size={18} />
                    </button>
                  </form>
                  {result && (
                    <div
                      className={`mt-5 rounded-2xl p-5 ${result.ok ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}
                      data-testid="coverage-checker-result"
                    >
                      {result.ok ? (
                        <div className="flex items-start gap-3">
                          <CheckCircle2 size={22} className="mt-0.5 shrink-0 text-emerald-600" />
                          <div>
                            <p className="font-display text-sm font-bold">¡Sí! Cubrimos {result.area}</p>
                            <p className="mt-1 text-sm">Agenda tu servicio sin costo de desplazamiento.</p>
                            <a
                              href={waLink(`¡Hola Colors Limpieza! Estoy en ${result.area} y quiero cotizar un servicio.`)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-xs font-bold text-white hover:bg-[#1EBE5A]"
                              data-testid="coverage-checker-whatsapp"
                            >
                              <MessageCircle size={15} /> Cotizar ahora
                            </a>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-3">
                          <AlertCircle size={22} className="mt-0.5 shrink-0 text-amber-600" />
                          <div>
                            <p className="font-display text-sm font-bold">No encontramos "{query}" en nuestra lista</p>
                            <p className="mt-1 text-sm">Es posible que igual lleguemos a tu zona. Escríbenos para confirmar.</p>
                            <a
                              href={waLink(`¡Hola Colors Limpieza! Estoy en ${query} y quiero saber si cubren mi zona.`)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-xs font-bold text-white hover:bg-[#1EBE5A]"
                              data-testid="coverage-checker-whatsapp"
                            >
                              <MessageCircle size={15} /> Confirmar mi zona
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 7. CTA FINAL */}
      <section id="cta-final" className="bg-white pb-16 md:pb-24" data-testid="final-cta-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-brand via-brand to-brand-dark px-6 py-16 text-center md:py-24">
              <img src="/logo.png" alt="Colors Limpieza" className="mx-auto h-20 w-20 rounded-full border-4 border-white/40 object-cover shadow-xl" />
              <h2 className="mx-auto mt-6 max-w-2xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                ¿Tus muebles, colchón o vehículo necesitan una limpieza profunda?
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/90">
                Recupera su apariencia y frescura sin salir de casa. Solicita tu cotización por WhatsApp.
              </p>
              <a
                href={waLink(DEFAULT_WA_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-10 py-4 font-display text-sm font-extrabold uppercase tracking-wider text-white shadow-[0_8px_30px_-5px_rgba(37,211,102,0.5)] transition-all hover:-translate-y-0.5 hover:bg-[#1EBE5A]"
                data-testid="final-cta-whatsapp-button"
              >
                <MessageCircle size={19} /> Hablar por WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
